import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// Rightwork's branded type-ahead: RightworkSelect's field and menu styling on
// MUI Autocomplete, for picking from a list where typing a new value is also
// allowed (freeSolo). ``value`` and ``onChange`` work on the field's text.
// ``onCommit`` fires with the final text when an option is picked or the
// field loses focus, for work that shouldn't run on every keystroke.
// ``fieldSx`` lets a form keep the closed field matching its neighbouring text
// inputs; the open menu is always branded. Labels should use
// id={`${id}-label`} and htmlFor={id}.
export default function RightworkAutocomplete({
  id, value, onChange, options, placeholder, ariaLabel, maxLength = 255, disabled = false, error = false, maxShown = 50,
  fieldSx, onCommit,
}) {
  return (
    <Autocomplete
      id={id}
      freeSolo
      options={options}
      inputValue={value}
      // Typing and picking an option both arrive here with the field's text.
      onInputChange={(_e, next) => onChange(next)}
      onChange={(_e, picked, reason) => {
        if (reason === "selectOption" && typeof picked === "string") onCommit?.(picked);
      }}
      onBlur={() => onCommit?.(value)}
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase();
        return (q ? opts.filter((o) => o.toLowerCase().includes(q)) : opts).slice(0, maxShown);
      }}
      disabled={disabled}
      size="small"
      componentsProps={{
        paper: {
          sx: {
            mt: 0.5,
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            "& .MuiAutocomplete-listbox": { p: 0.5, maxHeight: 320 },
            "& .MuiAutocomplete-option": {
              borderRadius: "6px", minHeight: 36, py: 1, px: 1.5, fontSize: "14px", color: "#334155",
            },
            "& .MuiAutocomplete-option.Mui-focused": { bgcolor: "#f8fafc" },
            "& .MuiAutocomplete-option[aria-selected='true']": { bgcolor: "#f5f3ff", color: "#6d28d9", fontWeight: 500 },
            "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": { bgcolor: "#ede9fe" },
            "& .MuiAutocomplete-option.Mui-focusVisible": { outline: "2px solid #6d28d9", outlineOffset: -2 },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={error}
          inputProps={{ ...params.inputProps, maxLength, "aria-label": ariaLabel }}
          sx={fieldSx || {
            "& .MuiOutlinedInput-root": {
              bgcolor: "white", color: "#334155", borderRadius: "8px", fontSize: "14px",
              "& fieldset": { borderColor: "#cbd5e1" },
              "&:hover fieldset": { borderColor: "#94a3b8" },
              "&.Mui-focused fieldset": { borderColor: "#6d28d9", borderWidth: 2 },
              "&.Mui-error fieldset": { borderColor: "#e11d48" },
              "&.Mui-disabled": { bgcolor: "#f1f5f9" },
            },
          }}
        />
      )}
    />
  );
}
